import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { getApiErrorMessage, type Partner } from '@/stores/partnerAuthStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface PartnerFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partner: Partner | null;
  onSaved: (savedPartner: Partner) => void;
}

/**
 * The form lives in its own component so that the `key` on it, rather than an
 * effect, is what resets the fields. DialogContent unmounts its children when
 * closed, so a fresh mount per subject gives every field its value from props
 * at first render instead of painting the previously edited partner for a frame
 * and correcting it afterwards.
 */
export function PartnerFormDialog({ open, onOpenChange, partner, onSaved }: PartnerFormDialogProps) {
  const isEdit = partner !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit partner' : 'Add partner'}</DialogTitle>
          <DialogDescription>
            {isEdit ? "Update this partner's details, role, or password." : 'Create a new back office partner account.'}
          </DialogDescription>
        </DialogHeader>
        <PartnerForm
          key={partner?.uuid ?? 'new'}
          partner={partner}
          onOpenChange={onOpenChange}
          onSaved={onSaved}
        />
      </DialogContent>
    </Dialog>
  );
}

type PartnerRole = 'admin' | 'support';
type FieldErrors = Partial<Record<'name' | 'email' | 'role' | 'password', string>>;

/** Mirrors PartnerController's store/update rules. */
const NAME_MAX = 255;
const EMAIL_MAX = 255;
const PASSWORD_MIN = 8;

function PartnerForm({
  partner,
  onOpenChange,
  onSaved,
}: Omit<PartnerFormDialogProps, 'open'>) {
  const isEdit = partner !== null;

  const [name, setName] = useState(partner?.name ?? '');
  const [email, setEmail] = useState(partner?.email ?? '');
  // Blank on create so the role is a deliberate choice, not a default that
  // silently hands out (or withholds) admin rights.
  const [role, setRole] = useState<PartnerRole | ''>(partner?.role ?? '');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  function validateEmail(value: string): string {
    if (!value.trim()) return 'Email is required.';
    if (value.length > EMAIL_MAX) return `Email must be at most ${EMAIL_MAX} characters.`;
    return EMAIL_PATTERN.test(value) ? '' : 'Enter a valid email address.';
  }

  function validate(): FieldErrors {
    const next: FieldErrors = {};
    if (!name.trim()) next.name = 'Name is required.';
    else if (name.length > NAME_MAX) next.name = `Name must be at most ${NAME_MAX} characters.`;
    const emailError = validateEmail(email);
    if (emailError) next.email = emailError;
    if (!role) next.role = 'Role is required.';
    // Required on create. On edit a blank password means "keep the current one"
    // and is not sent, but a typed one must still meet the minimum.
    if (!isEdit && !password) next.password = 'Password is required.';
    else if (password && password.length < PASSWORD_MIN) {
      next.password = `Password must be at least ${PASSWORD_MIN} characters.`;
    }
    return next;
  }

  function clearError(field: keyof FieldErrors) {
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setIsSubmitting(true);
    try {
      if (isEdit) {
        const body: Record<string, string> = { name, email, role };
        if (password) body.password = password;
        const { data } = await api.patch(`/partners/${partner!.uuid}`, body);
        onSaved(data.data);
      } else {
        const { data } = await api.post('/partners', { name, email, role, password });
        onSaved(data.data);
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <div className="space-y-2">
        <Label htmlFor="partner-name" required>Name</Label>
        <Input
          id="partner-name"
          value={name}
          maxLength={NAME_MAX}
          aria-invalid={!!errors.name}
          onChange={(e) => {
            setName(e.target.value);
            clearError('name');
          }}
          required
        />
        {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
      </div>
      <div className="space-y-2">
        <Label htmlFor="partner-email" required>Email</Label>
        <Input
          id="partner-email"
          type="email"
          value={email}
          maxLength={EMAIL_MAX}
          aria-invalid={!!errors.email}
          onChange={(e) => {
            setEmail(e.target.value);
            clearError('email');
          }}
          onBlur={(e) =>
            setErrors((prev) => ({ ...prev, email: e.target.value ? validateEmail(e.target.value) || undefined : undefined }))
          }
          required
        />
        {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
      </div>
      <div className="space-y-2">
        <Label htmlFor="partner-role" required>Role</Label>
        <select
          id="partner-role"
          value={role}
          aria-invalid={!!errors.role}
          onChange={(e) => {
            setRole(e.target.value as PartnerRole);
            clearError('role');
          }}
          className="flex h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:ring-destructive"
          required
        >
          <option value="" disabled>
            -- Select role --
          </option>
          <option value="admin">Admin</option>
          <option value="support">Support</option>
        </select>
        {errors.role && <p className="text-xs text-destructive">{errors.role}</p>}
      </div>
      <div className="space-y-2">
        <Label htmlFor="partner-password" required={!isEdit}>
          {isEdit ? 'New password (leave blank to keep current)' : 'Password'}
        </Label>
        <Input
          id="partner-password"
          type="password"
          value={password}
          aria-invalid={!!errors.password}
          onChange={(e) => {
            setPassword(e.target.value);
            clearError('password');
          }}
          minLength={PASSWORD_MIN}
          required={!isEdit}
        />
        {errors.password && <p className="text-xs text-destructive">{errors.password}</p>}
      </div>
      <DialogFooter>
        <Button type="submit" variant="accent" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </DialogFooter>
    </form>
  );
}
